import { useContext } from "react";
import LazyLoad from "react-lazyload";

import dynamic from "next/dynamic";

import PropTypes from "prop-types";

import { LayoutContext } from "common/contexts";

import ButtonNew from "components/atoms/ButtonNew/index";
import Img from "components/atoms/Img";
import LinkRouter from "components/atoms/LinkRouter";
import Title from "components/atoms/Title";
import PageType from "components/molecules/PageType";
import BankingOptions from "components/organisms/BankingOptions";
import { noteTraditional } from "components/organisms/BankingOptions/bankNote";
import Banner from "components/organisms/Banner";
import OnlineBankingTool from "components/organisms/OnlineBankingTool";
import OpenBankAccount from "components/organisms/OpenBankAccount";
import RequiredDocument from "components/organisms/RequiredDocument";
import Walkthrough from "components/organisms/Walkthrough";

import BannerBankingTool from "./banner-banking-tool/index";
const NewsSlider = dynamic(() => import("components/organisms/NewsSlider"), {
  ssr: false,
});

const OpenBankAccountTemplate = ({ data, dataJson, dataBank }) => {
  const { locationRouter } = useContext(LayoutContext);
  const {
    country,
    hide_note,
    no_select,
    dataPageType,
    dataHowToOpen,
    dataOpenBankFee,
  } = dataJson;

  return (
    <>
      <Banner
        minHeightBanner="banner-min-h-635"
        breadcrumbs={dataJson.breadcrumbs}
        title={`Open bank account for <br/>${country} Company`}
        titleProps={{
          className: "banner-default-title mb-10 capitalize",
          textColor: "banner-title-default-color",
        }}
        dataTrustpilot={{
          dataStatic: dataJson.dataTrustpilot,
          dataDefault: data.dataTrustpilot,
        }}
        leftClassName="lg:col-7"
        rightClassName="lg:col-5"
        rightContent={
          <Img
            src={
              dataJson?.imgBanner
                ? dataJson?.imgBanner
                : "/assets/images/common/open-bank.png"
            }
            className="w-full"
            wrapper={{
              className: `bbc-banner-image-country lg:pb-0 ${
                locationRouter === "offshore" ? "pb-[98px]" : "pb-10"
              }`,
            }}
            width={591}
            height={412}
            priority={true}
            alt={dataJson?.imgAlt}
          />
        }
        bgColor={"bg-bbc-country"}
      />
      {dataPageType && (
        <div className="absolute left-1/2 -translate-x-1/2 -translate-y-[74px] text-center lg:-translate-y-1/2">
          <PageType data={dataPageType} />
        </div>
      )}

      {dataHowToOpen && (
        <section className="flex flex-col">
          <div className="container order-2 lg:order-1">
            <Title
              text={dataHowToOpen.title}
              className="bbc-title mt-10 mb-6 text-center capitalize lg:mt-20 lg:mb-10"
            />
            <div
              className={`paragraph ${
                country.toLowerCase() === "singapore" && "blue-marker"
              }`}
            >
              {dataHowToOpen.content.map((item, index) => (
                <div key={index} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </div>
            {dataHowToOpen.note ? (
              <div
                dangerouslySetInnerHTML={{ __html: dataHowToOpen.note }}
              ></div>
            ) : (
              <>
                <p className=" mt-4 border-l-4 border-bbc-brand-700 pl-2 font-medium ">
                  Wonder which can be the best bank for your {country} company?
                  Let&apos;s find out with our{" "}
                  <LinkRouter
                    href="/guide-and-tool/banking-tool"
                    target="_blank"
                    className="text-bbc-brand-500"
                  >
                    Banking Tool
                  </LinkRouter>
                </p>
              </>
            )}
          </div>
          {dataJson.dataOnlineBankingTool &&
            typeof dataJson.dataOnlineBankingTool === "object" &&
            Object.keys(dataJson.dataOnlineBankingTool).length > 0 && (
              <div className="order-1 mt-0 mb-0 lg:order-2 lg:mt-10">
                <OnlineBankingTool data={dataJson.dataOnlineBankingTool} />
              </div>
            )}
        </section>
      )}

      {dataJson.fullWalkthrough && (
        <section className="mt-10 lg:mt-20">
          <div className="container">
            <Walkthrough data={dataJson.fullWalkthrough} />
          </div>
        </section>
      )}

      {dataJson.bankingTool && (
        <BannerBankingTool data={dataJson.bankingTool} />
      )}

      {dataOpenBankFee &&
        Object.keys(dataOpenBankFee).length > 0 &&
        dataOpenBankFee.bank_fee.length > 0 && (
          <section className="pb-10 lg:pb-20">
            <OpenBankAccount
              data={dataOpenBankFee}
              country={country}
              title={dataOpenBankFee.title}
            />
          </section>
        )}
      <section>
        <div className="container">
          <BankingOptions
            title={`Banking Options for ${country} Company`}
            noSelect={no_select}
            dataTraditional={dataBank.traditional}
            dataEMI={dataBank.emi}
            tabArray={dataBank?.tabArray}
            noteTraditional={
              // Hide_note
              //   ? ""
              //   : "**Initial deposit is often blocked in the offshore bank account; if so, this amount is often small.<br/> However, banks in Hong Kong and Singapore do not freeze this amount, so you can use it freely. But you will be charged an additional fee for not maintaining your account above min balance.<br/>* Remote opening applies for Singapore-incorporated companies only."
              noteTraditional(dataBank.traditional, hide_note)
            }
          />
          {!hide_note && locationRouter === "offshore" && country === "BVI" && (
            <div className="relative mt-10 flex h-auto w-full flex-col items-end justify-between overflow-hidden rounded-[24px] bg-[#EFF6FF] drop-shadow-lg lg:mt-[100px] lg:min-h-[414px] lg:flex-row">
              <div className="lg:[250px] relative h-[280px]  w-full md:h-[150px]">
                <div className="absolute -left-[250px] -top-[400px] z-0 h-[800px] w-[800px] rounded-full bg-bbc-gradient-15 md:-left-[80px] md:-top-[400px] lg:-left-[120px] lg:-top-[540px] lg:bg-bbc-gradient-16"></div>
              </div>
              <div className="absolute left-0 top-0 bottom-0 w-11/12 py-8 px-4 lg:w-2/3 lg:py-20 lg:px-10 xl:py-[100px] xl:px-[65px]">
                <p className="text-2xl font-semibold text-bbc-white lg:text-[30px] lg:leading-[44px]">
                  Still unsure and need more banking options for your BVI
                  company?
                </p>
                <p className="mb-6 text-2xl font-semibold text-bbc-white lg:mb-10 lg:text-[30px] lg:leading-[44px]">
                  Find out with our{" "}
                  <span className=" text-bbc-brand-700">Banking Tool</span>
                </p>
                <div className="pt-2">
                  <ButtonNew
                    as="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="/offshore/guide-and-tool/banking-tool"
                    className="rounded-full bg-bbc-white px-6 py-2 font-semibold uppercase text-bbc-primary drop-shadow-xl hover:text-bbc-brand-700"
                  >
                    Try it now
                  </ButtonNew>
                </div>
              </div>
              <div className="bottom-0 z-10 flex lg:absolute lg:right-6 xl:right-[65px]">
                <Img
                  src="/assets/images/offshore/banking-support/trynow-bvi-banking.png"
                  alt="placeholder"
                  width={398}
                  height={384}
                />
              </div>
            </div>
          )}
          {!hide_note && locationRouter === "offshore" && country !== "BVI" && (
            <div className="mt-10 text-center">
              <p className="mb-6">
                Need more references? Here is a Banking Tool to make your
                decision easier!
              </p>
              <ButtonNew
                as="a"
                target="_blank"
                rel="noopener noreferrer"
                href="/offshore/guide-and-tool/banking-tool"
              >
                Try Now
              </ButtonNew>
            </div>
          )}
        </div>
      </section>
      <section>
        <RequiredDocument
          title={dataJson.dataRequiredDocument.title || "Required Documents"}
          data={dataJson.dataRequiredDocument}
          sectionClassName="py-10 lg:py-20"
          backgroundImage="/assets/images/common/required-documents.png"
        />
      </section>
      <section className="mb-0">
        <div className="container">
          <Title className="title-h2 m-auto mb-10 w-2/3 pb-0 text-center text-bbc-brand-800">
            Industry News & Insights
          </Title>
          <LazyLoad>
            <NewsSlider data={data} />
          </LazyLoad>
        </div>
      </section>
    </>
  );
};

export default OpenBankAccountTemplate;

OpenBankAccountTemplate.propTypes = {
  data: PropTypes.object,
  posts: PropTypes.array,
};

OpenBankAccountTemplate.defaultProps = {
  data: {},
  posts: [],
};
